import { Divider, Stack } from '@mui/material';
import { type FlowAutomatedVoiceBlockConfig, FlowDurationUnits } from 'flow-schema';
import {
  AUTOMATED_VOICE_VOICES,
  AutomatedVoiceVoicemailAction,
  getAutomatedVoice,
} from 'primary-schema';
import { useState } from 'react';

import { AutomatedVoiceCallerPhoneNumberFormField } from './CallerPhoneNumberFormField';
import {
  BlockValueTemplateField,
  BlockValueTemplateFieldArea,
} from '../../ConfigFields/CodeMirror/FlowTemplate';
import { BlockDurationField } from '../../ConfigFields/Duration';
import { BlockSelectField } from '../../ConfigFields/SelectField';
import { BlockSwitchField } from '../../ConfigFields/Switch';
import { BlockTextField } from '../../ConfigFields/TextField';
import { useSelectedNodeField } from '../../ConfigFields/useSelectedNodeField';
import { CommunicationWindowFormFields } from '../shared/CommunicationWindow';

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
];

export function AutomatedVoiceFormFields() {
  const { value: language = 'en' } =
    useSelectedNodeField<FlowAutomatedVoiceBlockConfig['language']>('config.language');
  const { setValue: setVoice } =
    useSelectedNodeField<FlowAutomatedVoiceBlockConfig['voice']>('config.voice');
  const [isCommunicationWindowExpanded, setIsCommunicationWindowExpanded] = useState(true);
  return (
    <Stack gap={2}>
      <BlockValueTemplateField
        fieldName="config.calleePhoneNumber"
        label="Recipient Phone Number"
        helperText="Provide a valid US or Canadian phone number in E.164 format (e.g. +14155551234 or +14165551234)"
        placeholder="+1-###-###-####"
        required
      />
      <AutomatedVoiceCallerPhoneNumberFormField />
      <BlockValueTemplateFieldArea
        fieldName="config.message"
        label="Message"
        helperText="Read aloud once the call is answered."
      />
      <BlockSwitchField
        label="End Call on Voicemail"
        fieldName="config.voicemailAction"
        toggleStates={{
          activeState: AutomatedVoiceVoicemailAction.EndCall,
          inactiveState: AutomatedVoiceVoicemailAction.LeaveMessage,
        }}
        helperText="For messages with PHI. The call stays silent until a person is detected, then plays the message. Voicemail and unclear answers are hung up on."
      />
      <BlockSelectField<FlowAutomatedVoiceBlockConfig['language']>
        fieldName="config.language"
        label="Language"
        options={LANGUAGE_OPTIONS}
        clearable={false}
        onChange={(event) => {
          setVoice(getAutomatedVoice({ language: event.target.value }).id);
        }}
      />
      <BlockSelectField
        fieldName="config.voice"
        label="Voice"
        options={AUTOMATED_VOICE_VOICES.filter((voice) =>
          voice.locale.startsWith(`${language}-`)
        ).map((voice) => ({ value: voice.id, label: voice.label }))}
        defaultValue={getAutomatedVoice({ language }).id}
        clearable={false}
      />
      <BlockTextField
        type="number"
        fieldName="config.voiceSpeed"
        label="Speed"
        helperText="From 0.5 to 2, where 1 is normal speed."
      />
      <BlockDurationField
        fieldName="config.timeout"
        unitFieldNameSuffix="units"
        required={true}
        allowedUnits={[
          FlowDurationUnits.Hours,
          FlowDurationUnits.Minutes,
          FlowDurationUnits.Seconds,
        ]}
        toggleLabel="Timeout"
        valueLabel="Timeout (stop waiting after duration)"
      />
      <Divider />
      <CommunicationWindowFormFields
        expanded={isCommunicationWindowExpanded}
        setIsExpanded={setIsCommunicationWindowExpanded}
        description="Set the valid windows during which calls can be placed. If a call is initiated outside of this window, the block will error out."
      />
    </Stack>
  );
}
